import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Mendable API Overview\ndescription: Mendable API allow you to use Mendable Search in your own applications.\n---\n\n\nWelcome to the Mendable API, with which you can provide detailed, accurate, and contextually relevant answers to your customers questions based on your own documentation. With our API, you can easily integrate Mendable's advanced language model capabilities into your own applications.\n\n## Key Features\n- Natural Language Understanding: Our AI model understands complex language patterns and delivers human-like responses.\n- Advanced Information Retrieval: Leverage both keyword-based and semantic search to find the most relevant information.\n- Customizable Responses: Adjust the temperature and search thresholds to fine-tune the AI's response to suit your needs.\n- Conversation Continuity: Maintain context by providing the history of a conversation, ensuring coherent and relevant answers.\n- Source Attribution: Get information about the documents used to generate the answers, including links to the original sources.\n\n## Getting Started\nTo start using the Mendable API, you will need an API key. If you don't have one, you can get one by going to [mendable.ai](https://mendable.ai) and clicking the 'Get Started' or 'Talk to Us' button.\n\nOnce you have your API key, you can access the Mendable API by making a POST request to the following [endpoint](/mendable-api/chat).\n\n\n## API Parameters\nThe Mendable API offers a variety of parameters to customize your requests. You can also provide conversation history to maintain context and improve the quality of the AI's responses. See a full list of parameters [here](/mendable-api/chat)\n\n\n## Sample Use Cases\n- Customer Support Chatbots: Improve your customer support experience by integrating Mendable API into your chatbot to provide accurate and detailed answers to user queries.\n- Virtual Assistants: Empower your virtual assistant with advanced language understanding and information retrieval capabilities.\n- FAQ Automation: Automatically generate answers to frequently asked questions for your website or application.\n- Knowledge Base Search: Provide your users with a powerful search experience to find the information they need.";
const filepath = "/mendable-api/overview.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
